import React, { useEffect, useState } from 'react';
import {
  Box,
  FormControl,
  FormControlLabel,
  FormLabel,
  InputLabel,
  MenuItem,
  Radio,
  RadioGroup,
  Select,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import { Dialog } from 'components';
import { useFeedback } from 'hooks/useFeedback';
import { useAuth, useNotifications } from 'hooks';
import { defaultFeedback, industryOptions, purposeOptions, targetingOptions } from './constants';
import { SelectChangeEvent } from '@mui/material/Select/SelectInput';

// feedback modal may be triggered after 1st, 3rd and 5th login
const feedbackLoginCounts = [1, 3, 5];

export const FeedbackModal = () => {
  const { setNotification } = useNotifications();

  const { createFeedbackMutation } = useFeedback({
    onSuccess: () => {
      setNotification('Danke für deine Angaben! Mit deinem Feedback gestaltest du Alloy Ads aktiv mit.', 'success');
    },
  });
  const { loginCount, isGeneralFeedbackSubmitted, isLoading } = useAuth();

  const isFeedbackShownInThisSession = localStorage.getItem('isFeedbackShownInThisSession') !== null;
  const [isFeedbackModalOpened, setFeedbackModalOpened] = useState(false);
  const [feedbackData, setFeedbackData] = useState(defaultFeedback);
  const isPrimaryTargetingOther = feedbackData.primaryTargeting === 'other';
  const isPurposeOther = feedbackData.purpose === 'other';

  const handleFeedbackModalClose = () => {
    setFeedbackData(defaultFeedback);
    setFeedbackModalOpened(false);
  };

  const handleSendFeedback = () => {
    createFeedbackMutation
      .mutateAsync({
        feedbackType: 'general',
        feedbackData,
      })
      .then(handleFeedbackModalClose);
  };

  useEffect(() => {
    if (!isLoading && loginCount !== undefined) {
      if (!isGeneralFeedbackSubmitted && !isFeedbackShownInThisSession && feedbackLoginCounts.includes(loginCount)) {
        setFeedbackModalOpened(true);

        // ensure that modal is shown only once per session
        localStorage.setItem('isFeedbackShownInThisSession', JSON.stringify(true));
      }
    }
    // run this effect only when the user is loaded
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [loginCount, isGeneralFeedbackSubmitted, isLoading]);

  useEffect(() => {
    if (isPrimaryTargetingOther) {
      setFeedbackData((prev) => ({ ...prev, primaryTargetingDescription: '' }));
    }
  }, [isPrimaryTargetingOther]);

  useEffect(() => {
    if (isPurposeOther) {
      setFeedbackData((prev) => ({ ...prev, purposeDescription: '' }));
    }
  }, [isPurposeOther]);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setFeedbackData((prev) => ({ ...prev, [e.target.name]: e.target.value }));
  };

  const handleSelectChange = (e: SelectChangeEvent) => {
    setFeedbackData((prev) => ({ ...prev, [e.target.name]: e.target.value }));
  };

  return (
    <Dialog
      size="sm"
      sx={{
        maxWidth: '600px',
        margin: '0 auto',
      }}
      open={isFeedbackModalOpened}
      title={
        <Stack direction="row" alignItems="center" alignContent="start">
          <Typography ml={2} variant="subtitle1" fontWeight={700}>
            Willkommen bei Alloy Ads!
          </Typography>
        </Stack>
      }
      content={
        <Box ml={2} mr={2}>
          <Typography variant="body1" mb={6}>
            <b>Bevor du loslegst: Wir möchten dich kurz kennenlernen</b> – damit wir Alloy Ads gezielt weiterentwickeln
            können.
          </Typography>
          <FormControl>
            <FormLabel focused={false} sx={(theme) => ({ color: theme.palette.grey[800], fontWeight: 700 })}>
              Wie möchtest du Alloy Ads nutzen?
            </FormLabel>
            <RadioGroup value={feedbackData.purpose} name="purpose" onChange={handleChange}>
              {purposeOptions.map((radio) => (
                <FormControlLabel key={radio.label} value={radio.value} control={<Radio />} label={radio.label} />
              ))}
            </RadioGroup>
          </FormControl>
          {feedbackData.purpose === 'other' ? (
            <FormControl fullWidth>
              <TextField
                value={feedbackData.purposeDescription}
                onChange={handleChange}
                label="Wie möchtest du Alloy Ads nutzen?"
                multiline
                name="purposeDescription"
                minRows={4}
                maxRows={8}
                fullWidth
                variant="outlined"
                size="small"
                sx={{
                  '& .MuiInputBase-root.MuiOutlinedInput-root': {
                    padding: '8px',
                    background: '#FFFFFF',
                  },
                  '& .MuiInputBase-input.MuiInputBase-inputMultiline': {
                    padding: '7px',
                  },
                }}
              />
            </FormControl>
          ) : null}
          <FormControl sx={{ marginTop: '16px' }}>
            <FormLabel focused={false} sx={(theme) => ({ color: theme.palette.grey[800], fontWeight: 700 })}>
              An wen richtest du deine Werbung hauptsächlich?
            </FormLabel>
            <RadioGroup value={feedbackData.primaryTargeting} name="primaryTargeting" onChange={handleChange}>
              {targetingOptions.map((radio) => (
                <FormControlLabel key={radio.label} value={radio.value} control={<Radio />} label={radio.label} />
              ))}
            </RadioGroup>
          </FormControl>
          {feedbackData.primaryTargeting === 'other' ? (
            <FormControl fullWidth>
              <TextField
                value={feedbackData.primaryTargetingDescription}
                onChange={handleChange}
                label="Wie möchtest du Alloy Ads nutzen?"
                multiline
                name="primaryTargetingDescription"
                minRows={4}
                maxRows={8}
                fullWidth
                variant="outlined"
                size="small"
                sx={{
                  '& .MuiInputBase-root.MuiOutlinedInput-root': {
                    padding: '8px',
                    background: '#FFFFFF',
                  },
                  '& .MuiInputBase-input.MuiInputBase-inputMultiline': {
                    padding: '7px',
                  },
                }}
              />
            </FormControl>
          ) : null}
          <FormControl fullWidth sx={{ marginTop: '16px' }}>
            <InputLabel focused={false}>In welcher Branche bist du tätig?</InputLabel>
            <Select
              value={feedbackData.industry}
              label="In welcher Branche bist du tätig?"
              name="industry"
              placeholder="Bitte wählen..."
              onChange={handleSelectChange}
            >
              {industryOptions.map((option) => (
                <MenuItem key={option.value} value={option.value}>
                  {option.label}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </Box>
      }
      primaryActionTitle="SENDEN"
      secondaryActionTitle="SPÄTER"
      onSecondaryActionClick={handleFeedbackModalClose}
      onPrimaryActionClick={handleSendFeedback}
      isPrimaryActionLoading={createFeedbackMutation?.isPending}
    />
  );
};
